/**
 * @author      David Gustavsson
 * @description This is the cache for the Skyview portal.
 */

import { MemoryManager } from '../memory-manager';
import { CacheElement } from './cache-element';
import OrthotileCache from './orthotile-cache';
import PointcloudCache from './pointcloud-cache';
import ScanChunkCache from './scan-chunk-cache';

// This is the cache. It contains a set of cacheElement,
// each individually keeping track of its content and timeout
export class Cache {
  private cache: { [id: string]: CacheElement | 'loading' } = {};
  private enabled = true;
  private defaultTimeout: number;

  constructor(defaultTimeout = 1 * 30 * 1000) {
    // a default cache timeout, should one not be supplied
    this.defaultTimeout = defaultTimeout;
  }

  addScanChunk(key: string, data: any, cacheTimeout?: number, size = 0) {
    if (this.enabled === false) {
      return;
    }

    this.add(
      new ScanChunkCache(
        key,
        data,
        cacheTimeout || this.defaultTimeout,
        this.clearCacheElement.bind(this),
        size,
      ),
    );
  }

  addPointcloud(key: string, data: any, cacheTimeout?: number, size = 0) {
    if (this.enabled === false) {
      return;
    }

    this.add(
      new PointcloudCache(
        key,
        data,
        cacheTimeout || this.defaultTimeout,
        this.clearCacheElement.bind(this),
        size,
      ),
    );
  }

  addOrthotile(key: string, data: any, cacheTimeout?: number, size = 0) {
    if (this.enabled === false) {
      return;
    }

    this.add(
      new OrthotileCache(
        key,
        data,
        cacheTimeout || this.defaultTimeout,
        this.clearCacheElement.bind(this),
        size,
      ),
    );
  }

  add(cacheElement: CacheElement) {
    this.clearCacheElement(cacheElement.key);
    this.allocateMemory(cacheElement.size);
    this.cache[cacheElement.key] = cacheElement;
  }

  allocateMemory(size: number) {
    const availableMemory = MemoryManager.getInstance().getAvailableMemory();

    // Here we allocate data as memory is not available
    if (availableMemory < size) {
      const oldestData = Object.keys(this.cache)
        .map((cur) => this.cache[cur] as CacheElement)
        .sort((a, b) => {
          if (a.ticker === b.ticker) {
            return 0;
          } else if (a.ticker === undefined) {
            return 1;
          } else if (b.ticker === undefined) {
            return -1;
          }
          return b.ticker - a.ticker;
        });

      // Remove the oldest data until there are available memory to add the new data
      for (
        let i = 0;
        i < oldestData.length && MemoryManager.getInstance().getAvailableMemory() < size;
        i++
      ) {
        this.clearCacheElement(oldestData[i].key);
      }
    }
  }

  get(key: string) {
    const element = this.cache[key];
    if (element instanceof CacheElement) {
      return element.getData();
    }
  }

  getAll() {
    return this.cache;
  }

  reset(key: string) {
    const element = this.cache[key];
    if (element instanceof CacheElement) {
      element.reset();
    }
  }

  disable() {
    this.enabled = true;
  }

  queue(key: string) {
    this.clearCacheElement(key);
    this.cache[key] = 'loading';
  }

  isCached(key: string): boolean {
    return this.cache[key] instanceof CacheElement;
  }

  isQueued(key: string) {
    return this.cache[key] === 'loading';
  }

  tick() {
    Object.keys(this.cache).map((key) => {
      const element = this.cache[key];
      if (element instanceof CacheElement) {
        element.tick();
      }
    });
  }

  clearCacheElement(key: string) {
    const element = this.cache[key];
    if (element instanceof CacheElement) {
      element.dispose();
      delete this.cache[key];
    }
  }

  clearCache() {
    Object.keys(this.cache).map((key) => this.clearCacheElement(key));
    this.cache = {};
  }
}
